// Bar chart of a figure per day. The bars are drawn in SVG, the text around them is HTML (so it
// keeps its size on a narrow screen), the summary is its accessible name and the same data comes
// as a table for anyone who prefers it.
import { useTranslation } from 'react-i18next';

export interface DayPoint {
  /** Calendar day, YYYY-MM-DD. */
  date: string;
  value: number;
}

const BAR = 10;
const HEIGHT = 100;

export function BarChart({
  title,
  valueLabel,
  points,
}: {
  title: string;
  /** Name of the column with the figures in the table. */
  valueLabel: string;
  points: DayPoint[];
}) {
  const { t, i18n } = useTranslation();
  const day = (date: string, long = false) =>
    new Intl.DateTimeFormat(i18n.language, {
      timeZone: 'UTC',
      day: 'numeric',
      month: long ? 'long' : 'short',
    }).format(new Date(`${date}T12:00:00Z`));
  const total = points.reduce((sum, point) => sum + point.value, 0);
  const peak = Math.max(0, ...points.map((point) => point.value));
  const first = points[0];
  const last = points.at(-1);
  if (!first || !last) return null;
  const label = t('users.chart.label', {
    title,
    from: day(first.date, true),
    to: day(last.date, true),
    total,
    peak,
  });

  return (
    <figure className="flex flex-col gap-2 rounded-xl border border-line bg-paper p-4">
      <figcaption className="flex flex-col">
        <strong>{title}</strong>
        <span className="text-sm text-ink-muted">{t('users.chart.peak', { count: peak })}</span>
      </figcaption>
      <svg
        role="img"
        aria-label={label}
        viewBox={`0 0 ${points.length * BAR} ${HEIGHT}`}
        preserveAspectRatio="none"
        className="h-40 w-full"
      >
        <line
          x1="0"
          x2={points.length * BAR}
          y1={HEIGHT - 0.5}
          y2={HEIGHT - 0.5}
          stroke="var(--field-border)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
        {points.map((point, index) => {
          const height = peak === 0 ? 0 : Math.max(2, (point.value / peak) * (HEIGHT - 4));
          return point.value === 0 ? null : (
            <rect
              key={point.date}
              x={index * BAR + 1}
              y={HEIGHT - 1 - height}
              width={BAR - 2}
              height={height}
              rx="1"
              fill="var(--brand)"
            />
          );
        })}
      </svg>
      <div aria-hidden="true" className="flex justify-between text-sm text-ink-muted">
        <span>{day(first.date)}</span>
        <span>{day(last.date)}</span>
      </div>
      <details>
        <summary className="min-h-11 cursor-pointer py-2 font-bold text-brand">
          {t('users.chart.data')}
        </summary>
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">{label}</caption>
            <thead>
              <tr className="border-b-2 border-line">
                <th scope="col" className="py-1 pr-4">
                  {t('users.chart.day')}
                </th>
                <th scope="col" className="py-1">
                  {valueLabel}
                </th>
              </tr>
            </thead>
            <tbody>
              {[...points].reverse().map((point) => (
                <tr key={point.date} className="border-b border-line">
                  <th scope="row" className="py-1 pr-4 font-normal">
                    {day(point.date, true)}
                  </th>
                  <td className="py-1">{point.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}
